import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { businessData } from '../mock';
import { useToast } from '../hooks/use-toast';

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    perfil: '',
    zona: '',
    comentario: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }
    
    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es obligatorio';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.telefono)) {
      newErrors.telefono = 'Introduce un teléfono válido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Introduce un email válido';
    }
    
    if (!formData.perfil) {
      newErrors.perfil = 'Selecciona tu perfil';
    }
    
    if (!formData.zona.trim()) {
      newErrors.zona = 'La zona es obligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Error en el formulario",
        description: "Por favor, corrige los errores indicados.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Mock form submission with enhanced feedback
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "¡Solicitud enviada con éxito!",
        description: `Hola ${formData.nombre}, nos pondremos en contacto contigo en las próximas 24 horas al ${formData.telefono}.`,
      });

      // Reset form
      setFormData({
        nombre: '',
        telefono: '',
        email: '',
        perfil: '',
        zona: '',
        comentario: ''
      });
      setErrors({});
    } catch (error) {
      toast({
        title: "Error al enviar",
        description: "Por favor, inténtalo de nuevo o contáctanos directamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.nombre && formData.telefono && formData.email && formData.perfil && formData.zona;

  return (
    <section id="contact-section" className="py-24 bg-gradient-to-br from-gray-900 to-green-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Solicita tu contacto personalizado
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Completa el formulario y nuestro equipo se pondrá en contacto contigo para iniciar el proceso
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
              <CardHeader>
                <CardTitle className="text-2xl font-bold">¿Por qué elegirnos?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Sin riesgos</h4>
                    <p className="text-gray-300 text-sm">Solo cobramos si hay acuerdo exitoso</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Proceso transparente</h4>
                    <p className="text-gray-300 text-sm">Te mantenemos informado en cada paso</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Soporte legal</h4>
                    <p className="text-gray-300 text-sm">Contratos supervisados por abogados</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold mb-1">Rapidez garantizada</h4>
                    <p className="text-gray-300 text-sm">Respuesta en menos de 24 horas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Methods */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-white">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-semibold">Teléfono</h4>
                  <a href={`tel:${businessData.phone}`} className="text-gray-300 hover:text-green-400 transition-colors">
                    {businessData.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4 text-white">
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-semibold">Email</h4>
                  <a href={`mailto:${businessData.email}`} className="text-gray-300 hover:text-green-400 transition-colors">
                    {businessData.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-white border-0 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Formulario de contacto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <Input
                      value={formData.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      placeholder="Tu nombre completo"
                      className={`h-12 border-2 transition-colors ${
                        errors.nombre ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}
                      aria-invalid={!!errors.nombre}
                      aria-describedby={errors.nombre ? "nombre-error" : undefined}
                    />
                    {errors.nombre && (
                      <p id="nombre-error" className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.nombre}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <Input
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => handleInputChange('telefono', e.target.value)}
                      placeholder="+34 600 123 456"
                      className={`h-12 border-2 transition-colors ${
                        errors.telefono ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}
                      aria-invalid={!!errors.telefono}
                      aria-describedby={errors.telefono ? "telefono-error" : undefined}
                    />
                    {errors.telefono && (
                      <p id="telefono-error" className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.telefono}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="tu@email.com"
                    className={`h-12 border-2 transition-colors ${
                      errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                    }`}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ¿Eres propietario o inquilino? *
                    </label>
                    <Select value={formData.perfil} onValueChange={(value) => handleInputChange('perfil', value)}>
                      <SelectTrigger className={`h-12 border-2 transition-colors ${
                        errors.perfil ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}>
                        <SelectValue placeholder="Selecciona tu perfil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="propietario">Propietario</SelectItem>
                        <SelectItem value="inquilino">Inquilino</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.perfil && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.perfil}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Zona deseada *
                    </label>
                    <Input
                      value={formData.zona}
                      onChange={(e) => handleInputChange('zona', e.target.value)}
                      placeholder="Madrid, Barcelona, Valencia..."
                      className={`h-12 border-2 transition-colors ${
                        errors.zona ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-green-500'
                      }`}
                      aria-invalid={!!errors.zona}
                      aria-describedby={errors.zona ? "zona-error" : undefined}
                    />
                    {errors.zona && (
                      <p id="zona-error" className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.zona}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Comentario adicional
                  </label>
                  <Textarea
                    value={formData.comentario}
                    onChange={(e) => handleInputChange('comentario', e.target.value)}
                    placeholder="Cuéntanos más sobre tus necesidades específicas..."
                    className="min-h-24 border-2 border-gray-200 focus:border-green-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 text-lg rounded-xl disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Enviando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" />
                      Quiero que me contacten
                    </div>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Al enviar este formulario, aceptas que nos pongamos en contacto contigo para ofrecerte nuestros servicios. 
                  Consulta nuestra <button className="text-green-600 hover:underline">política de privacidad</button>.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;